import { create } from "zustand";
import { orderNorthSouthWestEast } from "../lib/pointNumbering";

// Point NUMBERS are the user-facing identity work order); ids are internal React/geojson keys and
// never shown.
export interface EditPoint {
  id: string;
  num: number;
  lng: number;
  lat: number;
}

export type EditTool = "add" | "move" | "delete" | "renumber";

// `geom` = the candidate's stack CELL polygon giving cell adjacency for medoid point-placement +
// graph-coloring the dots; absent on the spatial-only fallback.
export interface SuggestCluster {
  lng: number;
  lat: number;
  cluster: number;
  geom?: GeoJSON.Geometry;
}

interface PointEditState {
  fieldId: string | null; // non-null = an edit session is active (for this field)
  points: EditPoint[];
  tool: EditTool;
  pendingSuggest: boolean; // start requested auto-suggest (Map Soil → High-Res, no existing samples)
  pendingGrid: boolean; // start requested a pure grid layout (Map Soil → Classic Grid, no existing samples)
  gridMode: boolean; // this session builds a Classic-Grid/IDW map (vs a High-Res ML map) — for the accuracy kind
  clusters: SuggestCluster[];
  designKeys: string[];
  start: (
    fieldId: string,
    opts?: { suggest?: boolean; grid?: boolean },
  ) => void;
  end: () => void;
  clearPendingSuggest: () => void;
  clearPendingGrid: () => void;
  setClusters: (clusters: SuggestCluster[], designKeys: string[]) => void;
  setTool: (t: EditTool) => void;
  add: (lng: number, lat: number) => void;
  remove: (id: string) => void;
  move: (id: string, lng: number, lat: number) => void;
  // Give ONE point a new number. If another point already holds it they SWAP (numbering
  // stays unique); a number nobody holds is simply assigned. num < 1 is ignored.
  renumber: (id: string, num: number) => void;
  renumberAll: () => void; // north→south, west→east (lib/pointNumbering)
  seed: (pts: { lng: number; lat: number }[]) => void; // replace the set (suggested points)
}

let idSeq = 0;
const nextId = () => `pe-${++idSeq}`;

export const usePointEditStore = create<PointEditState>((set) => ({
  fieldId: null,
  points: [],
  tool: "add",
  pendingSuggest: false,
  pendingGrid: false,
  gridMode: false,
  clusters: [],
  designKeys: [],
  start: (fieldId, opts) =>
    set({
      fieldId,
      points: [],
      tool: "add",
      pendingSuggest: opts?.suggest ?? false,
      pendingGrid: opts?.grid ?? false,
      gridMode: opts?.grid ?? false,
      clusters: [],
      designKeys: [],
    }),
  end: () =>
    set({
      fieldId: null,
      points: [],
      tool: "add",
      pendingSuggest: false,
      pendingGrid: false,
      gridMode: false,
      clusters: [],
      designKeys: [],
    }),
  clearPendingSuggest: () => set({ pendingSuggest: false }),
  clearPendingGrid: () => set({ pendingGrid: false }),
  setClusters: (clusters, designKeys) => set({ clusters, designKeys }),
  setTool: (tool) => set({ tool }),
  add: (lng, lat) =>
    set((s) => ({
      points: [
        ...s.points,
        {
          id: nextId(),
          num: Math.max(0, ...s.points.map((p) => p.num)) + 1,
          lng,
          lat,
        },
      ],
    })),
  remove: (id) => set((s) => ({ points: s.points.filter((p) => p.id !== id) })),
  move: (id, lng, lat) =>
    set((s) => ({
      points: s.points.map((p) => (p.id === id ? { ...p, lng, lat } : p)),
    })),
  renumber: (id, num) =>
    set((s) => {
      if (
        !Number.isFinite(num) ||
        num < 1 ||
        !s.points.some((p) => p.id === id)
      )
        return s;
      const n = Math.round(num);
      const cur = s.points.find((p) => p.id === id)!.num;
      return {
        points: s.points.map((p) =>
          p.id === id ? { ...p, num: n } : p.num === n ? { ...p, num: cur } : p,
        ),
      };
    }),
  renumberAll: () =>
    set((s) => ({
      points: orderNorthSouthWestEast(s.points).map((p, i) => ({
        ...p,
        num: i + 1,
      })),
    })),
  seed: (pts) =>
    set({
      points: pts.map((p, i) => ({
        id: nextId(),
        num: i + 1,
        lng: p.lng,
        lat: p.lat,
      })),
    }),
}));
